import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { ColumnDefinition } from "tabulator-tables";
import { Lists } from "../../API/Lists";
import { Users } from "../../API/Users";
import { httpAll } from "../../API/utils/httpAll";
import ReusableModal from "../../components/ReusableModal";
import Tabulator from "../../components/Tabulator";
import { useFeedback } from "../../customHooks/useFeedback";

export default function Cloner() {
  const userAPI = useMemo(() => new Users(), []);
  const feedback = useFeedback();
  const [usersFrom, setUsersFrom] = useState<any>(null);
  const [usersTo, setUsersTo] = useState<any>(null);
  const listsAPI = useMemo(() => new Lists(), []);
  const [isUsersLoadingFrom, setIsUserLoadingFrom] = useState(false);
  const [isUsersLoadingTo, setIsUserLoadingTo] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [lists, setLists] = useState<any>(null);
  const [source, setSource] = useState<any>(null);
  const targetTableRef = useRef<any>(null);

  const userColumns: ColumnDefinition[] = useMemo(
    () => [
      { title: "client", field: "group", widthGrow: 2, hozAlign: "left" },
      {
        title: "First name",
        field: "firstName",
        widthGrow: 1.5,
        hozAlign: "left",
      },
      {
        title: "Last name",
        field: "lastName",
        widthGrow: 1.5,
        hozAlign: "left",
      },
      { title: "Email", field: "email", widthGrow: 2.5, hozAlign: "left" },
    ],
    []
  );
  const listsColumns: ColumnDefinition[] = useMemo(
    () => [
      {
        widthGrow: 0.5,
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        hozAlign: "center",
        headerSort: false,
        field: "id",
        title: "Selection",
        cellClick: function (e, cell) {
          cell.getRow().toggleSelect();
        },
      },
      { title: "Name", field: "name", widthGrow: 2, hozAlign: "left" },
      { title: "Type", field: "type", widthGrow: 2, hozAlign: "center" },
    ],
    []
  );

  const [target, setTarget] = useState<any>(null);
  const onSelectTarget = useCallback((arr: any[]) => {
    setTarget(arr[0]);
  }, []);

  const [selectedList, setSelectedList] = useState<any[]>([]);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const _cloneCreateCollections = useCallback(
    async (response: any, targetAccount: any) => {
      let raw: any = null;
      let position: any = null;
      let requests: any = {};
      let collections: any = [];
      let toBeCloned: any = null;
      for (let i = 0, length = response.length; i < length; i++) {
        raw = response[i];
        console.log(raw);
        toBeCloned = {
          benchmark: raw.benchmark != null ? raw.benchmark.symbol : null,
          currency: raw.currency,
          name: raw.name,
          ownerId: targetAccount.id,
          positions: [],
          type: raw.type,
          weightsDate: raw.weightsDate,
        };
        for (let j = 0, lengthJ = raw.positions.length; j < lengthJ; j++) {
          position = raw.positions[j];
          toBeCloned.positions.push({
            symbol: position.symbol,
            weight: position.weight,
          });
        }
        collections.push(toBeCloned);
        requests[raw.name] = listsAPI.create(toBeCloned, targetAccount.id);
      }
      const createResponse = await httpAll(requests);
      //unico errore che potrebbe esserci: il target possiede già una collection con quel nome
      let errors: string[] = [];
      let success: string[] = [];
      for (let collectionName in createResponse) {
        if (createResponse[collectionName].status === "KO") {
          errors.push(collectionName);
        } else {
          success.push(collectionName);
        }
      }
      setShowConfirmationModal(false);
      setTarget(null);
      const tabulatorInstance = targetTableRef.current.tabulatorInstance();
      tabulatorInstance.deselectRow();

      if (errors.length > 0 && success.length > 0) {
        //warning
        feedback.show({
          type: "warning",
          message: `[${success.join(
            ", "
          )}] portfolios cloned successfully, [${errors.join(", ")}] failed`,
        });
      } else if (errors.length > 0 && success.length === 0) {
        feedback.show({
          //error
          type: "error",
          message: `[${errors.join(
            ", "
          )}] have failed on cloning, check if this collections already exists in the target's account!`,
        });
      } else {
        //success
        feedback.show({
          type: "success",
          message: `[${success.join(", ")}] portfolios cloned successfully`,
        });
      }
    },
    [feedback, listsAPI]
  );

  const onClone = useCallback(async () => {
    const listProperties = [
      "benchmark",
      "currency",
      "id",
      "ownerId",
      "positions",
      "weightsDate",
    ];
    const selectedListsIds = selectedList.map((item) => item.id);
    let listsData: any[] = await listsAPI.portfolioFetch(
      selectedListsIds,
      listProperties
    );
    for (let i = 0; i < listsData.length; i++) {
      let listDataItem = listsData[i];
      const temp = selectedList.filter(
        (item) => item.id === listDataItem.id
      )[0];
      listDataItem = { ...listDataItem, ...temp };
      listsData[i] = listDataItem;
    }
    _cloneCreateCollections(listsData, target);
  }, [_cloneCreateCollections, listsAPI, selectedList, target]);
  const getAccounts = useCallback(
    async (value: string) => {
      const searches: any[] = [];
      if (value.length > 0) {
        let _value: string = "%" + value + "%"; // string contains

        searches.push({
          dimension: "email",
          segments: [_value],
        });

        searches.push({
          dimension: "firstName",
          segments: [_value],
        });

        searches.push({
          dimension: "lastName",
          segments: [_value],
        });
        let activeUsersId = await userAPI.getForSearch(searches);
        activeUsersId = activeUsersId.data.ids;

        let data = await userAPI.fetchUserProperties(
          ["firstName", "lastName", "group", "email"],
          activeUsersId
        );
        return (data = data.data.rows);
      }
    },
    [userAPI]
  );

  //#region - FROM
  const [searchContentFrom, setSearchContentFrom] = useState("");
  const [timerFrom, setTimerFrom] = useState<any>(null);
  const onSearchChangeFrom = useCallback(
    (e: any) => {
      const text = e.target.value;
      setSearchContentFrom(text);

      if (timerFrom) {
        clearTimeout(timerFrom);
        setTimerFrom(null);
      }
      setTimerFrom(
        setTimeout(async () => {
          if (text.length > 0) {
            setIsUserLoadingFrom(true);
            let value = await getAccounts(text);
            setUsersFrom(value);
            setIsUserLoadingFrom(false);
          }
        }, 500)
      );
    },
    [getAccounts, timerFrom]
  );
  const clearSearchContentFrom = useCallback(() => {
    setSearchContentFrom("");
    setLists(null);
    setUsersFrom(null);
  }, []);
  const onSearchEndButtonClickFrom = useCallback(() => {
    if (searchContentFrom.length > 1) {
      clearSearchContentFrom();
    } else {
      getAccounts(searchContentFrom);
    }
  }, [clearSearchContentFrom, getAccounts, searchContentFrom]);

  //#endregion

  //#region - TO
  const [searchContentTo, setSearchContentTo] = useState("");
  const [timerTo, setTimerTo] = useState<any>(null);
  const onSearchChangeTo = useCallback(
    (e: any) => {
      const text = e.target.value;
      setSearchContentTo(text);

      if (timerTo) {
        clearTimeout(timerTo);
        setTimerTo(null);
      }
      setTimerTo(
        setTimeout(async () => {
          if (text.length > 0) {
            setIsUserLoadingTo(true);
            let value = await getAccounts(text);
            setUsersTo(value);
            setIsUserLoadingTo(false);
          }
        }, 500)
      );
    },
    [getAccounts, timerTo]
  );
  const clearSearchContentTo = useCallback(() => {
    setSearchContentTo("");
    setUsersTo(null);
  }, []);
  const onSearchEndButtonClickTo = useCallback(() => {
    if (searchContentTo.length > 1) {
      clearSearchContentTo();
    } else {
      getAccounts(searchContentTo);
    }
  }, [clearSearchContentTo, getAccounts, searchContentTo]);
  //#endregion

  const onSelectLists = useCallback(
    (arrSelectedLists: any[]) => {
      setSelectedList(arrSelectedLists);
      if (arrSelectedLists.length === 0) {
        clearSearchContentTo();
      }
    },
    [clearSearchContentTo]
  );

  const onSelectUser = useCallback(
    async (selectedArr: any) => {
      setTarget(null);
      setSelectedList([]);
      try {
        if (selectedArr.length > 0) {
          setSource(selectedArr[0]);
          setIsListLoading(true);
          const listsIds = await listsAPI.getLists(selectedArr[0].id);
          const _lists = await listsAPI.portfolioFetch(listsIds, [
            "name",
            "type",
          ]);
          if (_lists.length > 0) {
            setLists(_lists);
            setIsListLoading(false);
          } else {
            setLists(null);
            setIsListLoading(false);
          }
        } else {
          clearSearchContentTo();
          setLists(null);
        }
      } catch (error) {
        setIsListLoading(false);
        setLists(null);
        alert("error on load lists in cloner");
      }
    },
    [clearSearchContentTo, listsAPI]
  );

  return (
    <Box
      height={"100%"}
      width={"100%"}
      overflow={"hidden"}
      flexDirection={"column"}
      display={"flex"}
      gap={1}
      px={2}
    >
      {target && source && (
        <ReusableModal
          buttons={[
            <Button onClick={() => setShowConfirmationModal(false)}>
              Cancel
            </Button>,
            <Button onClick={onClone} color="secondary">
              Clone
            </Button>,
          ]}
          modalTitle={<Typography>Clone portfolios and baskets</Typography>}
          children={
            <Box
              display={"flex"}
              p={1}
              flexDirection={"column"}
              gap={1}
              position={"relative"}
            >
              <Typography>
                From{" "}
                <strong>
                  {source.firstName} {source.lastName}
                </strong>{" "}
                ({source.email}) you are cloning:
              </Typography>
              <Box display={"flex"} overflow={"auto"} maxHeight={500}>
                <ul>
                  {selectedList.map((item, index) => (
                    <li key={index}>
                      <Typography>
                        {item.name} ({item.type})
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
              <Typography>
                To{" "}
                <strong>
                  {target.firstName} {target.lastName}
                </strong>{" "}
                ({target.email})
              </Typography>
            </Box>
          }
          isOpen={showConfirmationModal}
          setIsOpen={() => setShowConfirmationModal(false)}
        />
      )}
      <Box
        height={"100%"}
        width={"100%"}
        overflow={"hidden"}
        display={"flex"}
        gap={1}
      >
        <Card
          sx={{
            boxShadow: 3,
            my: 1,
            flex: 1,
            overflow: "hidden",
            position: "relative",
            display: "flex",
          }}
        >
          <CardContent
            sx={{
              overflow: "hidden",
              height: "100%",
              p: 0,
              minHeight: 0,
              maxHeight: "100%",
              width: "100%",
              gap: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography>From</Typography>

            <TextField
              placeholder="search for an account"
              size="small"
              fullWidth
              value={searchContentFrom}
              onChange={onSearchChangeFrom}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={onSearchEndButtonClickFrom}>
                    {searchContentFrom.length > 0 ? (
                      <ClearIcon />
                    ) : (
                      <SearchIcon />
                    )}
                  </IconButton>
                ),
              }}
            />

            {isUsersLoadingFrom ? (
              <Box
                width={"100%"}
                height={"100%"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={1}
                flexDirection={"column"}
              >
                <CircularProgress color="secondary" />
                <Typography>Loading accounts</Typography>
              </Box>
            ) : !isUsersLoadingFrom && usersFrom ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                minHeight={0}
                height={"100%"}
                maxHeight={"100%"}
                overflow={"hidden"}
                width={"100%"}
              >
                <Box
                  minHeight={0}
                  height={"100%"}
                  maxHeight={"100%"}
                  overflow={"auto"}
                  width={"100%"}
                >
                  <Tabulator
                    selectableRows={1}
                    data={usersFrom}
                    columns={userColumns}
                    onSelectRows={onSelectUser}
                  />
                </Box>
              </Box>
            ) : null}
          </CardContent>
        </Card>

        <Card
          sx={{
            boxShadow: 3,
            my: 1,
            flex: 1,
            overflow: "hidden",
            position: "relative",
            display: "flex",
          }}
        >
          <CardContent
            sx={{
              overflow: "hidden",
              height: "100%",
              p: 0,
              minHeight: 0,
              maxHeight: "100%",
              width: "100%",
            }}
          >
            {isListLoading ? (
              <Box
                width={"100%"}
                height={"100%"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={1}
                flexDirection={"column"}
              >
                <CircularProgress color="secondary" />
                <Typography>Loading portfolios and baskets</Typography>
              </Box>
            ) : !isListLoading && lists ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                minHeight={0}
                height={"100%"}
                maxHeight={"100%"}
                overflow={"hidden"}
                width={"100%"}
              >
                <Typography p={1}>Baskets and portfolios</Typography>
                <Box
                  minHeight={0}
                  height={"100%"}
                  maxHeight={"100%"}
                  overflow={"auto"}
                  width={"100%"}
                >
                  <Tabulator
                    selectableRows={true}
                    data={lists}
                    columns={listsColumns}
                    onSelectRows={onSelectLists}
                  />
                </Box>
              </Box>
            ) : null}
          </CardContent>
        </Card>

        <Card
          sx={{
            boxShadow: 3,
            my: 1,
            flex: 1,
            overflow: "hidden",
            position: "relative",
            display: "flex",
          }}
        >
          <CardContent
            sx={{
              overflow: "hidden",
              height: "100%",
              p: 0,
              minHeight: 0,
              maxHeight: "100%",
              width: "100%",
            }}
          >
            {selectedList.length > 0 && usersFrom && (
              <Box
                display={"flex"}
                flexDirection={"column"}
                minHeight={0}
                height={"100%"}
                maxHeight={"100%"}
                overflow={"hidden"}
                width={"100%"}
                gap={1}
              >
                <Typography>Clone to</Typography>
                <TextField
                  placeholder="search for an account"
                  size="small"
                  fullWidth
                  value={searchContentTo}
                  onChange={onSearchChangeTo}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={onSearchEndButtonClickTo}>
                        {searchContentTo.length > 0 ? (
                          <ClearIcon />
                        ) : (
                          <SearchIcon />
                        )}
                      </IconButton>
                    ),
                  }}
                />

                {isUsersLoadingTo ? (
                  <Box
                    width={"100%"}
                    height={"100%"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={1}
                    flexDirection={"column"}
                  >
                    <CircularProgress color="secondary" />
                    <Typography>Loading accounts</Typography>
                  </Box>
                ) : (
                  !isUsersLoadingTo &&
                  usersTo && (
                    <Box
                      minHeight={0}
                      height={"100%"}
                      maxHeight={"100%"}
                      overflow={"auto"}
                      width={"100%"}
                    >
                      <Tabulator
                        ref={targetTableRef}
                        selectableRows={1}
                        data={usersTo}
                        columns={userColumns}
                        onSelectRows={onSelectTarget}
                      />
                    </Box>
                  )
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      {target && (
        <Box px={1} display={"flex"} justifyContent={"flex-end"}>
          <Button
            color="secondary"
            onClick={() => setShowConfirmationModal(true)}
          >
            Clone
          </Button>
        </Box>
      )}
    </Box>
    // </Grid>
  );
}
